/* eslint-disable @typescript-eslint/no-loop-func */
/**
 * Vertical Carousel Component
 * @module patterns/components/carousels/vertical-carousel
 */

import { type App } from '~/app';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Vertical Carousel Component');

    const listeners: Array<[Document | Element | Window, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => {
            mountVerticalCarousel(listeners, '.vertical-carousel', 'active-indicator');
        })
        .catch((error: ErrorOptions) => {
            throw new Error('[Stockland FE] Unable to register Stockland Vertical Carousel Component', error);
        });

    return function destroyVerticalCarousel() {
        unMountVerticalCarousel(listeners);

        app.logger.debug('[DXA] Vertical Carousel Component destroyed');
    };
};

export function mountVerticalCarousel(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    componentSelector: string,
    activeIndicatorClass: string,
    root: HTMLElement | null = null
) {
    let rootNode: Document | HTMLElement = document;
    if (root) rootNode = root;

    const verticalSliderContainers = rootNode.querySelectorAll<HTMLElement>(componentSelector);

    for (const verticalSliderContainer of verticalSliderContainers) {
        const slideContainer = verticalSliderContainer.querySelector<HTMLElement>('.slides');
        const slides = verticalSliderContainer.querySelectorAll<HTMLElement>('.slide');
        const indicatorContainer = verticalSliderContainer.querySelector<HTMLElement>('.indicators');
        const indicators = verticalSliderContainer.querySelectorAll<HTMLElement>('.indicator');
        const variant = verticalSliderContainer.dataset.variant;
        const heading = verticalSliderContainer.querySelector<HTMLElement>('.heading');
        const headingHeight = heading ? heading.clientHeight : 0;

        // Functions
        const removeAllActiveIndicator = () => {
            for (const indicator of indicators) {
                indicator.classList.remove('!bg-primary');
            }
        };

        const removeAllActiveSlides = () => {
            for (const slide of slides) {
                slide.classList.add('lg:opacity-60');
            }
        };

        const setActiveIndicator = (targetIndex: number) => {
            removeAllActiveIndicator();
            const activeIndicator = indicators[targetIndex];
            activeIndicator?.classList.add('!bg-primary');
        };

        const setActiveSlides = (targetIndex: number) => {
            removeAllActiveSlides();
            const activeSlides = slides[targetIndex];
            activeSlides?.classList.remove('lg:opacity-60');
        };

        const addIndicatorClick = (indicator: HTMLElement, target: HTMLElement | null) => {
            //prevent default anchor link navigation and add smooth scrolling to sections
            indicator.addEventListener('click', (event) => {
                event.preventDefault();
                const navigation = document.querySelector<HTMLElement>('.navigation');
                const navigationHeight = navigation ? navigation.clientHeight : 0;
                const targetTop = target ? target.getBoundingClientRect().top : 0;

                if (variant === 'collapse') {
                    // Scroll container into view
                    const containerY =
						verticalSliderContainer.getBoundingClientRect().top + window.scrollY - navigationHeight;

                    // Scroll target slide into view
                    const slideOffset = target ? target.offsetTop : 0;
                    const distance = slideOffset - headingHeight;

                    window.scrollTo({ top: containerY, behavior: 'smooth' });
                    verticalSliderContainer.scrollTo({ top: distance, behavior: 'smooth' });
                } else {
                    target?.scrollIntoView({
                        behavior: 'smooth',
                    });

                    const targetY = targetTop + window.scrollY - navigationHeight - headingHeight;
                    window.scrollTo({ top: targetY, behavior: 'smooth' });
                }
            });
        };

        const initialiseVerticalSliderContainer = () => {
            // Set up default layouts for both variants
            if (slideContainer && indicatorContainer) {
                const slide = slideContainer.querySelector<HTMLElement>('.slide')

                if (variant === 'collapse') {
                    // 40px is the slides top and bottom margin
                    const containerHeight = 40 * 2 + slideContainer.scrollHeight / slides.length;

                    // 60px is the additional padding on the heading
                    const headingPadding = heading ? 60 : 0;

                    verticalSliderContainer.style.height =
						window.innerWidth > 1024 ? `${containerHeight + headingPadding}px` : 'auto';

                    // //Always set the indicator at the middle of the first tile
                    if (slide) {
                        indicatorContainer.style.top= `${slide.offsetHeight / 2 - indicatorContainer.offsetHeight / 2 + headingHeight }px`;
                    }

                    // console.log(
                    // 	parseInt(window.getComputedStyle(verticalSliderContainer).getPropertyValue('padding-top') ?? 0),
                    // 	'pad'
                    // );
                } else {
                    setActiveIndicator(0);
                    const navigation = document.querySelector<HTMLElement>('.navigation');
                    const navigationHeight = navigation ? navigation.clientHeight : 0;

                    if (heading) {
                        heading.style.top = `${navigationHeight}px`;
                    }

                    if (slide) {
                        indicatorContainer.style.top= '72px';
                        indicatorContainer.style.paddingTop = `${
                            headingHeight + slide.offsetHeight / 2 - indicatorContainer.offsetHeight / 2 
                        }px`;
                    }
                }
            }

            let space = 0;
            if (indicatorContainer && variant !== 'collapse') {
                space = -250;
            }

            // Set up intersection observer
            const observer = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            const slidesArray = [...slides];
                            const target = entry.target as HTMLElement;
                            const index = slidesArray.indexOf(target);
                            setActiveIndicator(index);
                            if (variant !== 'collapse') {
                                setActiveSlides(index);
                            }
                        }
                    }
                },
                {
                    root: variant === 'collapse' ? verticalSliderContainer : null,
                    threshold: 0.75,
                    rootMargin: `0px 0px ${space}px 0px`,
                }
            );

            // Add intersection observer
            for (const [index, currentIndicator] of indicators.entries()) {
                const currentAnchorBlock = slides[index];
                addIndicatorClick(currentIndicator, currentAnchorBlock);
                if (currentAnchorBlock) {
                    observer.observe(currentAnchorBlock);
                }
            }
        };
		
        setTimeout(() => {
            initialiseVerticalSliderContainer();
        }, 150);

        window.addEventListener('resize', initialiseVerticalSliderContainer);
        listeners.push([window, 'resize', initialiseVerticalSliderContainer]);
    }
}

export function unMountVerticalCarousel(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}
